/**
 * EditorList — full-featured list editor with CRUD and reordering
 */
import { RenderIcon } from '@/components/preview/RenderIcon';

interface FieldDef {
  key: string;
  type?: 'text' | 'textarea' | 'checkbox';
  placeholder?: string;
  default?: string;
}

interface EditorListProps {
  label: string;
  items: any[];
  onChange: (items: any[]) => void;
  fields: FieldDef[];
  maxItems?: number;
}

export function EditorList({ label, items, onChange, fields, maxItems = 10 }: EditorListProps) {
  const safeItems = Array.isArray(items) ? items : [];

  function addItem() {
    const newItem: any = {};
    fields.forEach((f) => {
      newItem[f.key] = f.default || '';
    });
    onChange([...safeItems, newItem]);
  }

  function updateItem(index: number, key: string, value: any) {
    const updated = safeItems.map((item, i) =>
      i === index ? { ...item, [key]: value } : item,
    );
    onChange(updated);
  }

  function removeItem(index: number) {
    onChange(safeItems.filter((_, i) => i !== index));
  }

  function moveItem(index: number, direction: -1 | 1) {
    const newItems = [...safeItems];
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= newItems.length) return;
    [newItems[index], newItems[targetIndex]] = [newItems[targetIndex], newItems[index]];
    onChange(newItems);
  }

  return (
    <div className="field">
      <label className="field-label">{label}</label>
      <div className="list-editor">
        {safeItems.map((item, idx) => (
          <div key={idx} className="list-item">
            <div className="list-item-content">
              {fields.map((f) => {
                if (f.type === 'checkbox') {
                  return (
                    <label key={f.key} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <input
                        type="checkbox"
                        checked={!!item[f.key]}
                        onChange={(e) => updateItem(idx, f.key, e.target.checked)}
                      />
                      {f.placeholder}
                    </label>
                  );
                }
                if (f.type === 'textarea') {
                  return (
                    <textarea
                      key={f.key}
                      className="field-input"
                      value={item[f.key] || ''}
                      onChange={(e) => updateItem(idx, f.key, e.target.value)}
                      placeholder={f.placeholder}
                      rows={2}
                    />
                  );
                }
                return (
                  <input
                    key={f.key}
                    className="field-input"
                    type="text"
                    value={item[f.key] || ''}
                    onChange={(e) => updateItem(idx, f.key, e.target.value)}
                    placeholder={f.placeholder}
                  />
                );
              })}
            </div>
            <div className="list-item-actions">
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => moveItem(idx, -1)}
                disabled={idx === 0}
                title="Nach oben"
              >
                &uarr;
              </button>
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => moveItem(idx, 1)}
                disabled={idx === safeItems.length - 1}
                title="Nach unten"
              >
                &darr;
              </button>
              <button
                className="btn btn-ghost btn-sm btn-danger"
                onClick={() => removeItem(idx)}
                title="Entfernen"
              >
                <RenderIcon name="trash" size={14} />
              </button>
            </div>
          </div>
        ))}
        {safeItems.length < maxItems && (
          <button className="btn btn-ghost btn-sm" onClick={addItem}>
            <RenderIcon name="plus" size={14} /> Hinzuf&uuml;gen
          </button>
        )}
      </div>
    </div>
  );
}
